import { ReactNode, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import tw from 'tailwind.macro';
import ImageTexture from '../../images/textures/texture-green.png';
import Button from '../Forms/Button';
import CheapTicketsTab from './cheapTicketsTab';
import HaveFunTab from './haveFunTab';
import messages from './messages';
import ReduceEmissionsTab from './reduceEmissionsTab';
import {
  TripComparatorCheapTicketsContext,
  TripComparatorProps,
  TripComparatorTabs,
  TripComparatorWidgetProps,
} from './types';

const HeaderContainer = styled.div`
  ${tw`bg-primary-400 w-full py-medium px-big flex overflow-x-auto whitespace-no-wrap`}
  gap: var(--size-small)
`;

const HeaderButtonStyle = css`
  ${tw`text-white`}

  &[data-is-selected='true'] {
    ${tw`bg-primary-500`}
  }
`;

type HeaderProps = {
  displayedTab: TripComparatorTabs;
  setDisplayedTab: (tab: TripComparatorTabs) => void;
};

const Header = (props: HeaderProps) => {
  const { displayedTab, setDisplayedTab } = props;
  const AVAILABLE_TABS = Object.values(TripComparatorTabs);

  return (
    <HeaderContainer>
      {AVAILABLE_TABS.map((tab, index) => (
        <Button
          key={index}
          css={HeaderButtonStyle}
          data-is-selected={tab === displayedTab}
          onClick={() => setDisplayedTab(tab)}
          size="MEDIUM"
          variant={tab === displayedTab ? 'PRIMARY' : 'TERCIARY'}
        >
          <FormattedMessage {...messages[tab]} />
        </Button>
      ))}
    </HeaderContainer>
  );
};

const TabWrapperContainer = styled.div<Pick<TabWrapperProps, 'isDisplayed'>>`
  ${({ isDisplayed }) => !isDisplayed && tw`hidden`}
  ${tw`px-big py-medium2`}
  background: url(${ImageTexture}),
    linear-gradient(97.78deg, #f5f4ec 0.37%, #edfdf9 92.55%);
`;

type TabWrapperProps = {
  isDisplayed: boolean;
  children: ReactNode;
};

const TabWrapper = (props: TabWrapperProps) => {
  const { children, isDisplayed } = props;

  return (
    <TabWrapperContainer isDisplayed={isDisplayed}>
      {children}
    </TabWrapperContainer>
  );
};

const TABS: Array<{
  name: TripComparatorTabs;
  children: (props: TripComparatorWidgetProps) => JSX.Element;
}> = [
  {
    name: TripComparatorTabs.CHEAP_TICKETS,
    children: CheapTicketsTab,
  },
  {
    name: TripComparatorTabs.HAVE_FUN,
    children: HaveFunTab,
  },
  {
    name: TripComparatorTabs.REDUCE_EMISSIONS,
    children: ReduceEmissionsTab,
  },
];

const TripComparator = (props: TripComparatorProps) => {
  const {
    destinationCity,
    distanceAsCrowFlies,
    onCheapTicketsContextChange,
    onDisplayedTabChange,
    originCity,
  } = props;
  const [displayedTab, setDisplayedTab] = useState<TripComparatorTabs>(
    TripComparatorTabs.CHEAP_TICKETS,
  );

  useEffect(() => {
    onDisplayedTabChange?.(displayedTab);
  }, [displayedTab, onDisplayedTabChange]);

  const onCheapTicketsContextChangeMemo = useCallback(
    (context: TripComparatorCheapTicketsContext) => {
      if (context.isLoading === false && context.hasPriceData === false) {
        setDisplayedTab(TripComparatorTabs.REDUCE_EMISSIONS);
      }

      onCheapTicketsContextChange?.(context);
    },
    [onCheapTicketsContextChange],
  );

  return (
    <div>
      <Header displayedTab={displayedTab} setDisplayedTab={setDisplayedTab} />
      {TABS.map((tab, index) => (
        <TabWrapper key={index} isDisplayed={displayedTab === tab.name}>
          <tab.children
            destinationCity={destinationCity}
            distanceAsCrowFlies={distanceAsCrowFlies}
            onCheapTicketsContextChange={onCheapTicketsContextChangeMemo}
            originCity={originCity}
          />
        </TabWrapper>
      ))}
    </div>
  );
};

export default TripComparator;
